<template>
  <div>
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Loading data...</p>
    </div>

    <div v-else-if="!items || items.length === 0" class="text-center py-5">
      <i class="fe fe-inbox text-muted" style="font-size: 48px; opacity: 0.6"></i>
      <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
    </div>

    <div v-else class="row">
      <div class="col-lg-3 col-md-6 col-sm-12 mb-4" v-for="course in items" :key="course.id">
        <div class="card h-100 shadow-sm border course-card" style="cursor: pointer" @click="onCardClick(course)">
          <div class="position-relative">
            <img
              :src="course.coaching?.cover_image || 'https://dummyimage.com/400x180/ffffff/000000&text=Course+Image'"
              :alt="course.coaching?.title"
              class="card-img-top"
              style="height: 180px; object-fit: cover"
            />

            <div class="position-absolute" style="top: 8px; left: 8px">
              <span :class="getTrainingStatusClass(course.status)">
                {{ snakeToTitle(course.status) }}
              </span>
            </div>

            <div class="position-absolute d-flex gap-2" style="bottom: 8px; left: 8px">
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon icon="file-earmark-bar-graph" style="font-size: 0.75rem"></b-icon>
                Gap
                <span :class="course.competency.difference < 1 ? 'badge bg-success' : 'badge bg-danger'">
                  {{ course.competency.difference }}
                </span>
              </div>
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon icon="star-fill" class="text-success" style="font-size: 0.75rem"></b-icon>
                <span>
                  {{ course.competency.proficiency_standard }}
                </span>
              </div>
              <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                <b-icon icon="star-fill" class="text-warning" style="font-size: 0.75rem"></b-icon>
                <span>
                  {{ course.competency.proficiency }}
                </span>
              </div>
            </div>
          </div>

          <div class="card-body p-3 gap-0 d-flex flex-column justify-content-between">
            <div>
              <span
                v-if="course.coaching?.coaching_type_name"
                :class="['badge-custom', badgeColorClass(course?.coaching?.coaching_type_id)]"
              >
                {{ course.coaching?.coaching_type_name || "-" }}
              </span>
            </div>

            <div>
              <h6 class="card-title font-weight-bold mb-0" style="font-size: 0.95rem; line-height: 1.3">
                {{ course.coaching?.title }}
              </h6>
            </div>

            <div class="d-flex flex-row justify-content-between align-items-center">
              <div class="d-flex align-items-center justify-content-start gap-2">
                <b-icon icon="person"></b-icon>
                <h5 class="mb-0">{{ course?.coaching?.coach_name || "-" }}</h5>
              </div>

              <div
                v-if="course.status === 'open'"
                class="d-flex align-items-center justify-content-start px-2 py-3"
                @click.stop
              >
                <input
                  type="checkbox"
                  class="form-check-input checkbox-lg cursor-pointer"
                  :checked="selectedItems.some((i) => i.id === course.id)"
                  @change.stop="onSelectChange(course, $event)"
                  aria-label="Select course"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isLoading && items.length > 0" class="mt-4 pt-3 border-top">
        <div class="row align-items-center">
          <div class="col-md-6">
            <p class="text-muted small mb-0">
              Showing {{ (page - 1) * pageSize + 1 }} to {{ Math.min(page * pageSize, totalItems) }} of
              {{ totalItems }} entries
            </p>
          </div>
          <div class="col-md-6">
            <nav aria-label="Course pagination">
              <!-- Pagination -->
              <ul class="pagination pagination-sm justify-content-end mb-0">
                <!-- Previous -->
                <li class="page-item" :class="{ disabled: page <= 1 }">
                  <button class="page-link" @click="changePage(page - 1)" :disabled="page <= 1">
                    <i class="fe fe-chevron-left"></i>
                  </button>
                </li>

                <template v-for="(item, idx) in visiblePages">
                  <!-- dots -->
                  <li v-if="item === '...'" :key="'dots-' + idx" class="page-item disabled">
                    <span class="page-link">...</span>
                  </li>

                  <!-- page number -->
                  <li v-else :key="'page-' + item" :class="['page-item', { active: item === page }]">
                    <button class="page-link" @click="changePage(item)">
                      {{ item }}
                    </button>
                  </li>
                </template>

                <!-- Next -->
                <li class="page-item" :class="{ disabled: page >= totalPages }">
                  <button class="page-link" @click="changePage(page + 1)" :disabled="page >= totalPages">
                    <i class="fe fe-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-4">
        <b-button
          className="mr-3"
          :disabled="!selectedItems.length"
          variant="primary"
          @click="$emit('open-confirm-submit-modal')"
        >
          <template>
            <span class="fe fe-save"></span>
            Submit
          </template>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { titleCase } from "@/utils/titleCase";
import { formatCurrency, snakeToTitle, getTrainingStatusClass, getTrainingTypeIcon } from "../utils/utils";

export default {
  name: "CardGridSocial",

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {};
  },
  computed: {
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.page;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      const add = (v) => pages.push(v);

      add(1);

      if (current > 3) add("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) add(i);

      if (current < total - 2) add("...");

      add(total);

      return pages;
    },
  },
  mounted() {},
  methods: {
    titleCase,
    getTrainingStatusClass,
    getTrainingTypeIcon,
    snakeToTitle,
    formatCurrency,

    onCardClick(course) {
      this.$emit("open-modal", course);
    },

    changePage(newPage) {
      if (newPage === this.page) return;
      if (newPage < 1 || newPage > this.totalPages) return;

      this.$emit("page-change", newPage);
    },

    onSelectChange(course, e) {
      this.$emit("select-change", course, e.target.checked);
    },

    badgeColorClass(type) {
      if (type === "M") return "badge-blue";
      return "badge-green";
    },
  },
};
</script>

<style>
.badge-custom {
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 0.25rem;
}

.course-description {
  font-size: 0.8rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.me-2 {
  margin-right: 0.5rem;
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

/* Tab Navigation */
.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  color: #000;
  background-color: transparent;
  border-bottom: 2px solid #dc2626;
}

.nav-tabs .nav-link:hover {
  border-bottom: 2px solid #dc2626;
}

/* Status Cards */
.status-card {
  border-radius: 12px;
  padding: 20px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-icon {
  width: 50px;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.status-card p {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.status-card h3 {
  font-size: 32px;
  font-weight: bold;
  margin: 0;
}

.bg-teal {
  background-color: #5bc0be;
}

/* Progress Bar */
.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
}

.progress-bar {
  font-weight: 600;
  font-size: 14px;
}

/* Course Card Styles */
.course-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

/* Badge Styles */
.badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.7rem;
}

.badge-custom {
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 0.25rem;
}

/* Soft Badge Colors */
.bg-danger-soft {
  background-color: #fce4e4;
}

.text-danger {
  color: #e63757 !important;
}

.bg-warning-soft {
  background-color: #fef5e4;
}

.text-warning {
  color: #f6c343 !important;
}

.bg-info-soft {
  background-color: #e4f4f7;
}

.text-info {
  color: #39afd1 !important;
}

/* Pagination */
.pagination .page-link {
  color: #2c7be5;
  border: 1px solid #dee2e6;
  padding: 0.375rem 0.75rem;
}

.pagination .page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.pagination .page-item.disabled .page-link {
  cursor: default;
  background-color: transparent;
  border-color: transparent;
  color: #6c757d;
  pointer-events: none;
}

.pagination .page-link:hover:not(.disabled) {
  background-color: #f8f9fa;
}

.pagination .page-item.active .page-link:hover {
  background-color: #d32f4f;
  border-color: #d32f4f;
}

.badge-green {
  background: var(--color-green-50, #e5fbf2);
  color: var(--color-green-600, #00ae65);
}

.badge-blue {
  background: var(--color-blue-100, #d7eff6);
  color: var(--color-blue-700, #0369a1);
}
</style>
